/// <reference path="../../typings/browser.d.ts" />

namespace Application.Controllers {
    'use strict';

    interface IRouteMobileDeviceParams {
        deviceId: string;
    }

    interface ISplittedTime {
        hours?: number;
        minutes?: number;
        seconds?: number;
    }

    interface IDataUsageEventData {
        href: string;
        iccid: string;
        id: number;
        simDataUsage: Models.ISIMDataUsages;
    }

    /**
     * MobileDevicesDashboardController class
     */
    export class MobileDevicesDashboardController {
        public static $name: string = "mobileDevicesDashboardController";
        public static $inject: string[] = ['$scope',
                                           '$routeParams',
                                           '$interval',
                                           '$q',
                                           'mobileDevicesService',
                                           'stompService',
                                           'streamStatusService',
                                           'networkNodeService',
                                           'healthService'];

        private static STATUS_INTERVAL: number = 60 * 1000;

        private statusInterval: ng.IPromise<void>;
        private streamStatus: Models.IStreamStatus;
        private deviceId: string;
        private deviceLoaded: boolean;
        private nodeInfoLoaded: boolean;
        private mobileDashboard: Models.IMobileDeviceDashboard;
        private remainingTime: ISplittedTime;
        private remainingSIMTime: ISplittedTime;
        private mapInfoSIM: Models.ISIMDashboard[];

        private totalBitrate: number;
        private totalSIMBitrate: number;
        private totalBitrateArray: number[];
        private totalSimBitrateArray: number[];
        private totalSIMRemainingData: number;

        private subscription: Services.IEventSubscription;

        /**
         * Constructor of the class.
         */
        constructor(private $scope: ng.IScope,
                    private $routeParams: ng.route.IRouteParamsService,
                    private $interval: ng.IIntervalService,
                    private $q: ng.IQService,
                    private mobileDeviceService: Services.MobileDevicesService,
                    private stompService: Services.StompService,
                    private streamStatusService: Services.StreamStatusService,
                    private networkNodeService: Services.NetworkNodeService,
                    private healthService: Services.HealthService) {
            
            this.nodeInfoLoaded = false;
            this.deviceLoaded = false;
            this.totalBitrate = 0;
            this.totalSIMBitrate = 0;
            this.totalBitrateArray = [];
            this.totalSimBitrateArray = [];
            this.remainingTime = {};
            this.remainingSIMTime = {};
            this.deviceId = (this.$routeParams as IRouteMobileDeviceParams).deviceId;         

            this.loadDeviceStatus();
            this.registerStompServices();
            this.initWatchers();
        }

        public getTotalDataRemaining(bytes: number): Models.IDataRemaining {
            return Common.Utils.humanReadableBytesAsObject(bytes);
        }

        public getEstimatedRemainingTime(remainingData: number): string {
            let seconds: number = parseInt((remainingData / this.totalBitrate).toFixed(0), 10);
            return this.totalBitrate > 0 ? Common.Utils.secsToHHMMSS(seconds) : "-";
        }

        public idFromHref(href: string): number {
            return Common.Utils.idFromHref(href);
        }

        public bytesToMpbs(bytes: number): number {
            return parseFloat((bytes * 8 / 1000000).toFixed(2));
        };

        private loadDeviceStatus(): void {
            this.mobileDeviceService.getMobileDeviceDashboard(this.deviceId)
                .then((mobileDashboard: Models.IMobileDeviceDashboard) => {
                    this.mobileDashboard = mobileDashboard;
                    (this.mobileDashboard.networkNode as Models.NetworkNode).health = 
                        this.healthService.getNodeHealth((this.mobileDashboard.networkNode as Models.NetworkNode).href);
                    this.getSIMInfo();
                    this.nodeInfoLoaded = true;
                    if (mobileDashboard.currentStreams.length > 0) {
                        this.refreshDeviceStatus();
                        this.startStatusInterval();
                    } else {
                        this.streamStatus = {
                            status: {
                                clientstatuspms: {
                                    data: {
                                        connectionstatus: [{
                                            ConnectionNo: 1,
                                            BitRate: 0,
                                            TransportType: "lan",
                                            LinkStatus: "config",
                                        }, {
                                            ConnectionNo: 2,
                                            BitRate: 0,
                                            TransportType: "wifi",
                                            LinkStatus: "config",
                                        }, {
                                            ConnectionNo: 3,
                                            BitRate: 0,
                                            TransportType: "qmiwwan",
                                            LinkStatus: "config",
                                        }, {
                                            ConnectionNo: 4,
                                            BitRate: 0,
                                            TransportType: "qmiwwan",
                                            LinkStatus: "config",
                                        }, {
                                            ConnectionNo: 5,
                                            BitRate: 0,
                                            TransportType: "qmiwwan",
                                            LinkStatus: "config",
                                        }, {
                                            ConnectionNo: 6,
                                            BitRate: 0,
                                            TransportType: "qmiwwan",
                                            LinkStatus: "config",
                                        }, {
                                            ConnectionNo: 7,
                                            BitRate: 0,
                                            TransportType: "usbmobile",
                                            LinkStatus: "config",
                                        }, {
                                            ConnectionNo: 8,
                                            BitRate: 0,
                                            TransportType: "usbmobile",
                                            LinkStatus: "config",
                                        }, {
                                            ConnectionNo: 9,
                                            BitRate: 0,
                                            TransportType: "usbmobile",
                                            LinkStatus: "config",
                                        }, {
                                            ConnectionNo: 10,
                                            BitRate: 0,
                                            TransportType: "usbmobile",
                                            LinkStatus: "config",
                                        }],
                                    },
                                },
                            },
                        };
                        this.deviceLoaded = true;
                    }
                });
        }

        private refreshDeviceStatus(): void {
            this.streamStatusService.getStreamStatus(this.mobileDashboard.currentStreams[0].streamId.toString())
                .then((streamStatus: Models.IStreamStatus) => {
                    this.streamStatus = streamStatus;
                    this.getInfoFromClientstatuspms();
                    this.updateRemainingTime();
                    this.deviceLoaded = true;
                });
        }

        private getSIMInfo(): void {
            this.totalSIMRemainingData = 0;
            this.mapInfoSIM = [];
            if (this.mobileDashboard.sims) {
                this.mobileDashboard.sims.forEach((sim: Models.ISIMDashboard) => {
                    this.totalSIMRemainingData += sim.remainingData;
                    this.mapInfoSIM[sim.slot] = sim;
                });
            }
        }

        private startStatusInterval(): void {
            if (this.statusInterval !== undefined)
                this.$interval.cancel(this.statusInterval);

            this.statusInterval = this.$interval(() => {
                this.refreshDeviceStatus();
            }, MobileDevicesDashboardController.STATUS_INTERVAL);
        }

        private getInfoFromClientstatuspms(): void {
            if (this.streamStatus.status.clientstatuspms !== undefined) {
                let totalSimBitrate: number = 0;
                let totalBitrate: number = 0;
                let cS: Models.IClientStatusConnectionStatus[] = this.streamStatus.status.clientstatuspms.data.connectionstatus;
                if (cS !== undefined) {
                    for (let nic of cS) {
                        if (nic.BitRate && nic.BitRate > 0) {
                            totalBitrate += nic.BitRate;
                            if (nic.TransportType !== 'lan' && nic.TransportType !== 'wifi' && nic.BitRate > 0) {
                                totalSimBitrate += nic.BitRate;
                            }
                        }
                    }
                }

                if (this.totalSimBitrateArray.length >= 10) this.totalSimBitrateArray.shift();
                if (this.totalBitrateArray.length >= 10) this.totalBitrateArray.shift();
                this.totalSimBitrateArray.push(totalSimBitrate);
                this.totalBitrateArray.push(totalBitrate);

                this.totalSIMBitrate = Math.floor(this.totalSimBitrateArray
                    .map((value: number, index: number, array: number[]) => {
                        return value / array.length;
                    })
                    .reduce((acc: number, current: number) => {
                        return acc + current;
                    }
                ));

                this.totalBitrate = Math.floor(this.totalBitrateArray
                    .map((value: number, index: number, array: number[]) => {
                        return value / array.length;
                    })
                    .reduce((acc: number, current: number) => {
                        return acc + current;
                    }
                ));
            }
        }

        private updateRemainingTime(): void { 
            let currentTransmissionTime: number = Math.floor(this.totalSIMRemainingData / this.totalSIMBitrate);
            if (currentTransmissionTime !== Infinity && isNaN(currentTransmissionTime)) {
                this.remainingTime.hours = Math.floor(currentTransmissionTime / 3600);
                currentTransmissionTime %= 3600;
                this.remainingTime.minutes = Math.floor(currentTransmissionTime / 60);
                this.remainingTime.seconds = Math.floor(currentTransmissionTime % 60);
            } else {
                this.remainingTime.hours = -1;
            }

            let simTransmissionTime: number = Math.floor(this.totalSIMRemainingData / this.totalBitrate);
            if (simTransmissionTime !== Infinity && isNaN(simTransmissionTime)) {
                this.remainingSIMTime.hours = Math.floor(simTransmissionTime / 3600);
                simTransmissionTime %= 3600;
                this.remainingSIMTime.minutes = Math.floor(simTransmissionTime / 60);
                this.remainingSIMTime.seconds = Math.floor(simTransmissionTime % 60);
            } else {
                this.remainingTime.hours = -1;
            }
        }

        private registerStompServices(): void {
            this.subscription = this.stompService.register(Models.MOBILE_DATA_COMPONENT, Models.SIM_USAGE_RECEIVED,
                (flexEvent: Models.IFlexEvent) => {
                    this.simDataReceived(flexEvent);
                });
        }

        private simDataReceived(flexEvent: Models.IFlexEvent): void {
            let dataUsage: IDataUsageEventData = (flexEvent.data as IDataUsageEventData);
            this.updateSimUsage(dataUsage);
        }

        private updateSimUsage(dataUsage: IDataUsageEventData): void {
            if (this.mobileDashboard.sims) {
                this.mobileDashboard.sims.forEach((sim: Models.ISIMDashboard) => {
                    if (sim.simId === Common.Utils.idFromHref(dataUsage.href)) {
                        if (dataUsage.simDataUsage.operationType === "INI")
                            sim.remainingData = dataUsage.simDataUsage.signedOperationValue;
                        else 
                            sim.remainingData += dataUsage.simDataUsage.signedOperationValue;
                    }
                });
                this.getSIMInfo();
                this.refreshDeviceStatus();
            }
        }

        private initWatchers(): void {
            this.$scope.$on('$destroy', () => {
                if (this.statusInterval)
                    this.$interval.cancel(this.statusInterval);

                if (this.subscription)
                    this.stompService.unregister(this.subscription);
            });
        }
    }
}